import React, {useEffect, useRef, useState} from 'react';
import {classes, style, vars} from './swiper.st.css';
import {DynamicRef} from '../dynamic-ref';
import {Edges, evaluateFunction, Values} from '@wix/devzai-utils-common';
import {reactCreateMemo} from '../react-utils/react-utils';
import {useFirstVisibleChildInViewport} from '../hooks';
import {useRefsUnion} from '../ref-utils';

export const SwiperSnapAlignment = {
    Start: 'start',
    Center: 'center',
} as const;

export type SwiperSnapAlignment = Values<typeof SwiperSnapAlignment>;

export const Swiper = reactCreateMemo(React.forwardRef(function Swiper (props: Swiper.Props, ref: React.Ref<HTMLDivElement>) {
    const {
        visibleItemsCount,
        snapAlignment = SwiperSnapAlignment.Start,
        snapToItemIndex = null,
        gap = 0,
        isRtl = false,

        style: styleProp,
        className,
        ...htmlAttributes
    } = props;

    const listViewRef = useRef<HTMLDivElement>(null);

    const refsUnion = useRefsUnion([listViewRef, ref])


    useEffect(() => {
        if (listViewRef ) {
            const index = snapToItemIndex !== null ? snapToItemIndex :
                snapAlignment === SwiperSnapAlignment.Center && listViewRef.current?.children.length ? Math.floor(listViewRef.current?.children.length / 2) :
                     0;

            const targetElement = listViewRef.current?.children?.item(index);

            if (targetElement) {
                listViewRef.current?.scrollTo({
                    left: evaluateFunction(() => {
                        const previousSibling = targetElement.previousElementSibling as HTMLElement | null;
                        if (previousSibling) {
                            return previousSibling.offsetLeft + previousSibling.offsetWidth * (isRtl ? -1 : 1);
                        } else {
                            return 0;
                        }
                    })
                })
            }
        }
    }, [snapAlignment, listViewRef, snapToItemIndex, isRtl])

    return (
        <div
            className={style(classes.root, {snapAlignment: snapAlignment}, className)}
            ref={refsUnion}
            style={{
                [vars.visibleItemsCount]: visibleItemsCount,
                [vars.gap]: `${gap}px`,
                ...styleProp
            }}
            {...htmlAttributes}
        />
    );
}));

export namespace Swiper {

    export interface Props extends React.HTMLAttributes<any> {
        visibleItemsCount: number;
        snapAlignment?: SwiperSnapAlignment;
        snapToItemIndex?: number | null;
        gap?: number;
        isRtl?: boolean;
    }
}

export function useSwiperNavigation<E extends Element> (
    elementRef: DynamicRef<E>,
    frameShrinking?: Edges<number>,
) {
    const firstVisibleChildInViewport = useFirstVisibleChildInViewport(elementRef, {
        frameShrinking: frameShrinking
    });

    const [targetElement, setTargetElement] = useState<E | null>(elementRef.current);

    const [slidesCount, setSlidesCount] = useState(0);

    elementRef.onRefWasChanged(element => {
        setTargetElement(element);
    })

    useEffect(() => {

        if (targetElement) {
            const mutationObserver = new MutationObserver(() => {
                setSlidesCount(targetElement.children.length);
            });

            mutationObserver.observe(targetElement, {childList: true, subtree: true});

            setSlidesCount(targetElement.children.length);

            return () => {
                mutationObserver.disconnect();
            }
        } else {
            return undefined;
        }

    }, [targetElement])

    return {
        slidesCount: slidesCount,
        currentSlide: firstVisibleChildInViewport
    }
}